import { Subject, fromEvent, map, filter, withLatestFrom } from "rxjs";
import { searchParametersActions } from 'features/searchResultsFacets';
import { store, store$ } from '@redux/store';

/**
 * The function `mapPageElement` returns the closest ancestor element with a `data-page` attribute, and
 * prevents the default behavior of the event.
 * @param event - The event parameter is an object that represents the event that occurred, such as a
 * click or keypress event. It contains information about the event, including the target element that
 * triggered the event.
 * @returns the page element that is closest to the target element with the attribute "data-page".
 */
const mapPageElement = (event)=> {
  const pageElement = event.target.closest('[data-page]');
  if (pageElement) {
     event.preventDefault()
  }
  return pageElement;
};

/**
 * This class is in charge ONLY of the logic to change the page number at the search parameters.
 */
class Pager{
  constructor() {
    const container = document.querySelector('[data-pagination]');
    const previousButton = container.querySelector('[data-previous]');
    const nextButton = container.querySelector('[data-next]');
    this.optionListElement = container.querySelector('[data-option-list]');
    
    const getPageFromState = (state) => parseInt(state.searchParameters?.results?.paging?.page || 1, 10);

    const previousButtonClick$ = fromEvent(previousButton, 'click').pipe(
      withLatestFrom(store$),
      map((dataArray) => getPageFromState(dataArray[1])),
    );
    const nextButtonClick$ = fromEvent(nextButton, 'click').pipe(
      withLatestFrom(store$),
      map((dataArray) => getPageFromState(dataArray[1])),
    );
    const optionClick$ = fromEvent(this.optionListElement, 'click').pipe(
      map(mapPageElement),
      filter(Boolean)
    );

    optionClick$.subscribe(this.optionClickHandler.bind(this));
    previousButtonClick$.subscribe(Pager.previousButtonClickHandler);
    nextButtonClick$.subscribe((currentPage) => Pager.nextButtonClickHandler(currentPage, 
      Number(this.optionListElement.dataset.pageCount)));
 
    this.pageChange$ = new Subject().pipe(
      withLatestFrom(store$),
      map((valuesArray) => {
        const pagination = {
          pageSelected: valuesArray[0],
          lastPage: valuesArray[1].searchParameters?.results?.paging?.page || 1
        }

        return pagination;
      }),
      filter(({ pageSelected, lastPage}) => pageSelected !== lastPage),
      map(({pageSelected}) => pageSelected)
    );

    this.pageChange$.subscribe((newPage) => store.dispatch(searchParametersActions.fetchPageNumber(newPage)));
  }

  /**
   * optionClickHandler: Handles when an option of the pagination is clicked.
   * @param {HTMLElement} optionElement Option element clicked.
   */
  optionClickHandler(optionElement) {    
    const newPage = parseInt(optionElement.dataset.page, 10);
    this.pageChange$.next(newPage);
  }

  /**
   * previousButtonClickHandler: Handles when the previous button is clicked.
   * @param {Number} currentPage Current page on the state.
   */
  static previousButtonClickHandler(currentPage) {
    const newPage = currentPage - 1;
    if (newPage < 1) {
      return;
    }

    store.dispatch(searchParametersActions.fetchPagingControlsPrev(newPage));
  }

  /**
   * nextButtonClickHandler: Handles when the next button is clicked.
   * @param {Number} currentPage Current page on the state.
   */
  static nextButtonClickHandler(currentPage, optionCount) {
    const newPage = currentPage + 1;
    
    if (newPage > optionCount) {
      return;
    }

    store.dispatch(searchParametersActions.fetchPagingControlsNext(newPage));
  }
}

export default Pager;
